import { Fragment } from 'react';
import type { ReactNode } from 'react';

type PolicyDocumentProps = {
  source: string;
};

function renderInline(value: string, keyPrefix: string): ReactNode[] {
  const pieces = value.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return pieces.map((piece, index) => {
    const key = `${keyPrefix}-${index}`;
    const bold = piece.startsWith('**') && piece.endsWith('**');
    const text = bold ? piece.slice(2, -2) : piece;
    const email = text === 'privacy@wellnessfrontier.ai';
    if (email) {
      const link = <a href="mailto:privacy@wellnessfrontier.ai">{text}</a>;
      return bold ? <strong key={key}>{link}</strong> : <Fragment key={key}>{link}</Fragment>;
    }
    return bold ? <strong key={key}>{text}</strong> : <Fragment key={key}>{text}</Fragment>;
  });
}

function isTableDivider(value: string) {
  return /^\|(?:\s*:?-+:?\s*\|)+$/.test(value);
}

function tableCells(value: string) {
  return value.slice(1, -1).split('|').map((cell) => cell.trim());
}

export function PolicyDocument({ source }: PolicyDocumentProps) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let index = lines[0]?.startsWith('# ') ? 1 : 0;
  let key = 0;

  while (index < lines.length) {
    const line = lines[index]?.trim() ?? '';
    if (!line) { index += 1; continue; }

    if (line.startsWith('>')) {
      while (index < lines.length && (lines[index]?.trim().startsWith('>') ?? false)) index += 1;
      continue;
    }

    if (/^#{2,3}\s/.test(line)) {
      const level = line.match(/^#+/)?.[0].length ?? 2;
      const text = line.replace(/^#{2,3}\s+/, '');
      blocks.push(level === 2
        ? <h2 key={`block-${key++}`}>{text}</h2>
        : <h3 key={`block-${key++}`}>{text}</h3>);
      index += 1;
      continue;
    }

    if (line.startsWith('- ')) {
      const items: string[] = [];
      while (index < lines.length && (lines[index]?.trim().startsWith('- ') ?? false)) {
        items.push((lines[index]?.trim() ?? '').slice(2));
        index += 1;
      }
      blocks.push(<ul key={`block-${key++}`}>{items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item, `list-${key}-${itemIndex}`)}</li>)}</ul>);
      continue;
    }

    if (line.startsWith('|') && (lines[index + 1] ? isTableDivider(lines[index + 1]!.trim()) : false)) {
      const headers = tableCells(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && (lines[index]?.trim().startsWith('|') ?? false)) {
        rows.push(tableCells(lines[index]!.trim()));
        index += 1;
      }
      blocks.push(
        <div className="policy-document__table-wrap" key={`block-${key++}`} tabIndex={0}>
          <table>
            <thead><tr>{headers.map((header, cellIndex) => <th key={cellIndex} scope="col">{header}</th>)}</tr></thead>
            <tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{renderInline(cell, `table-${rowIndex}-${cellIndex}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }

    const paragraph = [line];
    index += 1;
    while (index < lines.length) {
      const next = lines[index]?.trim() ?? '';
      if (!next || next.startsWith('#') || next.startsWith('>') || next.startsWith('- ') || next.startsWith('|')) break;
      paragraph.push(next);
      index += 1;
    }
    blocks.push(<p key={`block-${key++}`}>{renderInline(paragraph.join(' '), `paragraph-${key}`)}</p>);
  }

  return (
    <article className="policy-document">
      {blocks}
    </article>
  );
}
